import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { PassWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/pass-widget/pass-widget.component';
import { FailWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/fail-widget/fail-widget.component';
import { PendingWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/pending-widget/pending-widget.component';
import { InspectionPassRateWidgetComponent } from '../../../../_shared/widgets/asset-compliance-tasks-widgets/inspection-pass-rate-widget/inspection-pass-rate-widget.component';
import { PreRoutineTypeChecklistComponent } from './trip-checklist/pre-routine-type-checklist/pre-routine-type-checklist.component';
import { PostRoutineTypeChecklistComponent } from './trip-checklist/post-routine-type-checklist/post-routine-type-checklist.component';
import {
  ChecklistType,
  RoutineComplianceChecklistSubmissionApi,
  TripChecklist,
} from '../../../models/inspection-checklist.model';
import {
  routineChecklistEntityKindFromLabel,
  routineChecklistTabTitleKeys,
} from '../../../../_shared/models/routine-checklist-display.model';
import {
  resolveInspectionTypeId,
  RoutineVideo,
  RoutineVideoKind,
  SubmissionVideoKind,
} from '../../../models/routine-video.model';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { TranslocoService } from '@jsverse/transloco';
import { translateChecklistTypeName, translateRoutineTypeName } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-asset-routine-compliance-details',
  standalone: true,
  imports: [
    RouterModule,
    PassWidgetComponent,
    FailWidgetComponent,
    PendingWidgetComponent,
    InspectionPassRateWidgetComponent,
    PreRoutineTypeChecklistComponent,
    PostRoutineTypeChecklistComponent
  ],
  templateUrl: './asset-routine-compliance-details.component.html',
  styleUrl: './asset-routine-compliance-details.component.scss'
})
export class AssetRoutineComplianceDetailsComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private transloco: TranslocoService = inject(TranslocoService);
  private readonly cancelSubject = new Subject<void>();
  private queryParamsSub?: Subscription;
  private readonly validTabs = new Set<ChecklistType>(['pre', 'post']);

  activeTab: ChecklistType = 'pre';
  assetName: string = '';
  assetId: number | null = null;
  complianceId: number | null = null;
  routineTypeId: number | null = null;
  routineComplianceTaskId: number | null = null;
  routineChecklistTypeLabel: string = '';

  showPreTab: boolean = false;
  showPostTab: boolean = false;

  isLoading: boolean = true;
  hasError: boolean = false;
  preDetails: TripChecklist[] = [];
  postDetails: TripChecklist[] = [];
  preVideos: RoutineVideo[] = [];
  postVideos: RoutineVideo[] = [];
  isVideosLoading: boolean = false;

  ngOnInit() {
    this.queryParamsSub = this.route.queryParamMap.subscribe((map) => {
      this.ingestQueryParams(map);
      void this.getRoutineComplianceChecklistAsync();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private ingestQueryParams(map: ParamMap): void {
    this.assetName = map.get('assetName') || '';
    this.assetId = this.getNumberFromParamMap(map, 'assetId');
    this.complianceId = this.getNumberFromParamMap(map, 'complianceId');
    this.routineTypeId = this.getNumberFromParamMap(map, 'routineTypeId');
    this.routineComplianceTaskId = this.getNumberFromParamMap(map, 'routineComplianceTaskId');
    this.routineChecklistTypeLabel = map.get('checklistType') ?? '';

    this.applyTabVisibility();

    const tab = this.normalizeTabQuery(map.get('tab'));
    if (tab && this.validTabs.has(tab) && this.isTabAllowed(tab)) {
      this.activeTab = tab;
    } else {
      this.activeTab = this.showPreTab ? 'pre' : 'post';
    }
  }

  toggleTab(tab: ChecklistType): void {
    if (this.activeTab !== tab && this.isTabAllowed(tab)) {
      this.activeTab = tab;
    }
  }

  private isTabAllowed(tab: ChecklistType): boolean {
    return tab === 'pre' ? this.showPreTab : this.showPostTab;
  }

  get preTabTitle(): string {
    const kind = routineChecklistEntityKindFromLabel(this.routineChecklistTypeLabel);
    return this.transloco.translate(routineChecklistTabTitleKeys(kind).pre);
  }

  get postTabTitle(): string {
    const kind = routineChecklistEntityKindFromLabel(this.routineChecklistTypeLabel);
    return this.transloco.translate(routineChecklistTabTitleKeys(kind).post);
  }

  get preInspectionTypeId(): number | null {
    return resolveInspectionTypeId(this.routineTypeId, 'pre');
  }

  get postInspectionTypeId(): number | null {
    return resolveInspectionTypeId(this.routineTypeId, 'post');
  }

  private normalizeTabQuery(raw: string | null): ChecklistType | null {
    if (!raw) {
      return null;
    }
    const v = raw.trim().toLowerCase();
    if (v === 'pre' || v === 'pretrip') {
      return 'pre';
    }
    if (v === 'post' || v === 'posttrip') {
      return 'post';
    }
    return null;
  }

  private applyTabVisibility(): void {
    const t = this.routineChecklistTypeLabel?.trim() ?? '';
    const hasPre = /\bpre\b/i.test(t);
    const hasPost = /\bpost\b/i.test(t);

    if (!t || (!hasPre && !hasPost)) {
      this.showPreTab = true;
      this.showPostTab = true;
      return;
    }

    this.showPreTab = hasPre;
    this.showPostTab = hasPost;
  }

  private getNumberFromParamMap(map: ParamMap, key: string): number | null {
    const value = Number(map.get(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  private async getRoutineComplianceChecklistAsync(): Promise<void> {
    if (!this.assetId || !this.routineTypeId || !this.routineComplianceTaskId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.cancelSubject.next();

    try {
      const response = await this.complianceOverviewService.getRoutineComplianceChecklistAsync({
        assetId: this.assetId,
        routineTypeId: this.routineTypeId,
        routineComplianceTaskId: this.routineComplianceTaskId,
        page: 1,
        pageSize: 10
      }, this.cancelSubject);

      const data = response.data;
      this.preDetails = (data?.pre?.results ?? []).map(r => this.mapSubmissionToRow(r));
      this.postDetails = (data?.post?.results ?? []).map(r => this.mapSubmissionToRow(r));
      this.isLoading = false;

      void this.loadVideosAsync();
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private async loadVideosAsync(): Promise<void> {
    this.isVideosLoading = true;
    const tasks: Promise<void>[] = [];

    if (this.showPreTab && this.preInspectionTypeId != null) {
      tasks.push(this.loadPhaseVideosAsync('pre', this.preInspectionTypeId).then(v => { this.preVideos = v; }));
    }
    if (this.showPostTab && this.postInspectionTypeId != null) {
      tasks.push(this.loadPhaseVideosAsync('post', this.postInspectionTypeId).then(v => { this.postVideos = v; }));
    }

    try {
      await Promise.all(tasks);
    } finally {
      this.isVideosLoading = false;
    }
  }

  private async loadPhaseVideosAsync(phase: ChecklistType, inspectionTypeId: number): Promise<RoutineVideo[]> {
    const taskId = this.routineComplianceTaskId;
    if (taskId == null) {
      return [];
    }

    const phaseLabel = this.transloco.translate(
      phase === 'pre'
        ? 'compliance.shared.routineChecklist.legLabels.trip.pre'
        : 'compliance.shared.routineChecklist.legLabels.trip.post'
    );
    const kinds: { kind: RoutineVideoKind; title: string; videoKind: SubmissionVideoKind }[] = [
      { kind: 'external', title: this.transloco.translate('compliance.overview.routineDetails.externalVideo'), videoKind: SubmissionVideoKind.Exterior },
      { kind: 'internal', title: this.transloco.translate('compliance.overview.routineDetails.internalVideo'), videoKind: SubmissionVideoKind.Interior }
    ];

    const results = await Promise.all(kinds.map(async ({ kind, title, videoKind }) => {
      const link = await this.complianceOverviewService
        .getSubmissionVideoLinkAsync(taskId, inspectionTypeId, videoKind)
        .catch(() => null);

      return {
        kind,
        title,
        phaseLabel,
        capturedAt: link?.data?.uploadedAt ?? '',
        videoUrl: link?.data?.url ?? '',
        inspectionTypeId,
        submissionVideoKind: videoKind
      } as RoutineVideo;
    }));

    return results;
  }

  private mapSubmissionToRow(r: RoutineComplianceChecklistSubmissionApi): TripChecklist {
    const uploads = r.attachmentCount === 0
      ? this.transloco.translate('compliance.overview.routineDetails.noDocuments')
      : this.transloco.translate('compliance.overview.routineDetails.documentCount', { count: r.attachmentCount });

    return {
      id: r.submissionId,
      checklistId: r.checklistId,
      checklist: r.checklistName,
      checklistType: r.routineTypeName,
      checklistTypeLabel: translateRoutineTypeName(this.transloco, r.routineTypeName),
      date: r.submittedAt,
      docChecklist: r.checklistTypeName,
      docChecklistLabel: translateChecklistTypeName(this.transloco, r.checklistTypeName),
      notes: (r.notes ?? '').trim() || this.transloco.translate('compliance.overview.routineDetails.noNoteProvided'),
      inspectionUploads: uploads,
      taskStatus: this.mapSubmissionStatus(r.submissionStatus),
    };
  }

  private mapSubmissionStatus(status: number): string {
    switch (status) {
      case 1: return 'Pending';
      case 2: return 'Pass';
      case 3: return 'Fail';
      default: return 'Pending';
    }
  }
}